import React, {
  useEffect, useReducer, useState,
} from 'react';
import {
  useForm,
  SubmitHandler,
  FormProvider,
} from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import reducer, { initialState, SleepioWellnessOnboardingStep } from './sleepioWellnessOnboardingState';
import ConfirmDetails from './steps/ConfirmDetails';
import { EligibilityFormCustomization } from '../Onboarding/OnboardingFlowContext';
import { SleepioWellnessOnboardingParams } from './SleepioWellnessOnboardingPage';

import CreatePassword from './steps/CreatePassword/CreatePassword';
import SleepioCoverageRejection from './steps/CoverageRejection';

import { ExpiredLinkError, PasswordNotSetError } from './steps/ErrorState';

import { ReadyToStartSleepioWellness } from './steps/ReadyToStartSleepioWellness/ReadyToStartSleepioWellness';

import {
  applyCoverageFieldsRefinement, buildEmployeeIdErrorMessage, createCoverageFieldsSchema,
} from '@/components/CoverageFields/coverageFieldsSchema';
import { PASSWORD_VALIDATION_REGEX, buildSkipConfirmDetailsSessionKey } from '@/utils/constants';

const INITIAL_FORM_DATA = {
  firstName: '',
  lastName: '',
  zipCode: '',
  dateOfBirth: '',
  email: '',
  phoneNumber: '',
  password: '',
  confirmPassword: '',
  employeeId: '',
};

type WellnessOnboardingDataMap = Record<keyof typeof INITIAL_FORM_DATA, string>;

const createSchema = (errorMap: WellnessOnboardingDataMap) => createCoverageFieldsSchema(errorMap).extend({
  email: z.union([
    z.literal(''),
    z.string({ required_error: errorMap.email })
      .email(),
  ]).optional(),
  phoneNumber: z.string({
    required_error: errorMap.phoneNumber,
  }).optional(),
});

type WellnessOnboardingData = z.infer<ReturnType<typeof createSchema>>;

type Props = {
  formCustomization: EligibilityFormCustomization;
}

const SleepioWellnessOnboarding = ({
  formCustomization,
}: Props) => {
  const { careTeamUid } = useParams<SleepioWellnessOnboardingParams>();
  // CoverageCheckWithEmail already validated these details, so this leg can skip straight to
  // CreatePassword - via sessionStorage, not a URL param, so a plain link can't skip the check.
  const skipConfirmDetails = sessionStorage.getItem(buildSkipConfirmDetailsSessionKey(careTeamUid)) === 'true';
  const [state, dispatch] = useReducer(reducer, skipConfirmDetails ? {
    ...initialState,
    step: 'CreatePassword',
    action: { type: 'SET_STEP', step: 'CreatePassword' },
  } : initialState);
  // One-shot: consumed above for the initial render, so it can't be reused by a later attempt.
  useEffect(() => {
    sessionStorage.removeItem(buildSkipConfirmDetailsSessionKey(careTeamUid));
  }, [careTeamUid]);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const { t } = useTranslation();
  const errorMap: WellnessOnboardingDataMap = {
    firstName: t('coverage.errors.first_name_error'),
    lastName: t('coverage.errors.last_name_error'),
    zipCode: t('coverage.errors.zipcode_error'),
    dateOfBirth: t('coverage.errors.dob_error'),
    email: t('coverage.errors.email_error'),
    phoneNumber: t('coverage.errors.phone_error'),
    password: t('coverage.errors.password_rules_error'),
    confirmPassword: t('coverage.errors.confirm_password_error'),
    employeeId: buildEmployeeIdErrorMessage(formCustomization, t),
  };

  const baseSchema = createSchema(errorMap);

  const passwordSchema = z.object({
    password: z.string({ required_error: errorMap.password })
      .regex(PASSWORD_VALIDATION_REGEX, { message: t('coverage.errors.password_rules_error') }),
    confirmPassword: z.string({ required_error: errorMap.confirmPassword }),
  });

  // Field names track baseSchema automatically; each stays a plain optional string (not
  // baseSchema's own rule, e.g. dateOfBirth's length check) since react-hook-form defaults
  // these to '' rather than undefined, and CreatePassword never fills them in either way.
  const looseCoverageFields = z.object(
    Object.fromEntries(Object.keys(baseSchema.shape).map((key) => [key, z.string().optional()])),
  );

  // CreatePassword only renders password/confirmPassword, so it must not re-require the rest.
  const refinedSchema = state.step === 'CreatePassword'
    ? looseCoverageFields.merge(passwordSchema).refine((data) => data.password === data.confirmPassword, {
      message: errorMap.confirmPassword,
      path: ['confirmPassword'],
    })
    : applyCoverageFieldsRefinement(baseSchema, formCustomization, {
      zipCode: errorMap.zipCode,
      employeeId: errorMap.employeeId,
    });

  const methods = useForm<WellnessOnboardingData>({
    defaultValues: INITIAL_FORM_DATA,
    mode: 'onBlur',
    resolver: zodResolver(refinedSchema, {}, { mode: 'async' }),
  });

  const { handleSubmit } = methods;

  const onSubmit: SubmitHandler<WellnessOnboardingData> = (data) => {
    console.log(data);
  };

  const renderStep = () => {
    switch (state.step) {
    case 'ConfirmDetails':
      return (
        <ConfirmDetails
          onNext={(step: SleepioWellnessOnboardingStep) => dispatch({ type: 'SET_STEP', step })}
          formCustomization={formCustomization}
          onError={() => dispatch({ type: 'SET_STEP', step: 'ExpiredLinkError' })}
        />
      );
    case 'CreatePassword':
      return (
        <CreatePassword
          onNext={(token: string) => {
            setAuthToken(token);
            dispatch({ type: 'SET_STEP', step: 'ReadyToStartSleepioWellness' });
          }}
          onError={() => dispatch({ type: 'SET_STEP', step: 'PasswordNotSetError' })}
          isGeneric={formCustomization.isGeneric}
        />
      );
    case 'ReadyToStartSleepioWellness':
      return <ReadyToStartSleepioWellness authToken={authToken} />;
    case 'CoverageRejection':
      return <SleepioCoverageRejection />;
    case 'PasswordNotSetError':
      return <PasswordNotSetError />;
    case 'ExpiredLinkError':
      return <ExpiredLinkError />;
    default:
      return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {renderStep()}
      </form>
    </FormProvider>
  );
};

export default SleepioWellnessOnboarding;
